/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useMutation } from "@apollo/client";
import getTabName from "components/company/valuation/getTabName";
import { SimpleTextTooltip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import ToggleBookmarkForIndicatorMutation from "lib/graphql/mutations/settings/ToggleBookmarkForIndicatorMutation";
import type {
  IToggleBookmarkForIndicatorMutation,
  IToggleBookmarkForIndicatorMutationVariables,
} from "lib/graphql/mutations/settings/__generated__/ToggleBookmarkForIndicatorMutation.generated";
import { BookmarkIcon, CircleHelpIcon, LineChartIcon } from "lucide-react";
import React from "react";
import { useIntl } from "react-intl";
import type { IValuationIndicator, IValuationIndicatorCategory } from "types/graphqlTypes";
import { IValuationIndicatorVerbiageLanguage } from "types/graphqlTypes";

interface IndicatorSnapshotProps {
  isAdjusted: boolean;
  indicator: IValuationIndicator;
  abbreviation: string;
  originalValue: number;
  formattedValue: string;
  description: string;
  category?: IValuationIndicatorCategory;
  isBookmarked: boolean;
  viewerCanBookmark: boolean;
  verbiageLanguage: IValuationIndicatorVerbiageLanguage;
  viewerCanSeeHistory: boolean;
  onToggleActive: () => void;
}

export default function IndicatorSnapshot({
  isAdjusted,
  indicator,
  abbreviation,
  originalValue,
  formattedValue,
  description,
  category,
  isBookmarked,
  viewerCanBookmark,
  verbiageLanguage,
  viewerCanSeeHistory,
  onToggleActive,
  ...props
}: IndicatorSnapshotProps) {
  const intl = useIntl();
  const rootClassName = classNames(
    "flex flex-col items-start",
    "space-y-2 p-4",
    "group relative",
    "focus:outline-none",
    "hover:cursor-pointer hover:rounded-md hover:bg-neutral-50 dark:hover:bg-slate-800/50",
    {
      "hover:cursor-auto": !viewerCanSeeHistory,
    },
  );

  const [toggleBookmarkForIndicator, { loading, error }] = useMutation<
    IToggleBookmarkForIndicatorMutation,
    IToggleBookmarkForIndicatorMutationVariables
  >(ToggleBookmarkForIndicatorMutation, {
    variables: {
      indicator,
    },
  });
  const hasError = !!error;

  const children = (
    <>
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center space-x-3">
          <h4
            className={classNames("text-2xl font-bold text-teal-700 dark:text-teal-400", {
              "text-purple-600 dark:text-purple-400": isAdjusted,
              "text-red-700 dark:text-red-400": originalValue < 0,
              "text-gray-400 dark:text-gray-500": originalValue === 0,
            })}
          >
            {formattedValue}
          </h4>

          {formattedValue === "—" && (
            <SimpleTextTooltip
              content={
                verbiageLanguage === IValuationIndicatorVerbiageLanguage.EnUs
                  ? "If any of the values that composes the formula distorts the calculation, the result will not be displayed. For example: losses, negative Equity or Cash > Debt."
                  : "Caso os valores que compõem a fórmula distorcerem o cálculo, o resultado não será exibido. Exemplo: prejuízo, Patrimônio negativo ou Caixa > Dívida."
              }
            >
              <CircleHelpIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </SimpleTextTooltip>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {viewerCanBookmark && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger
                  asChild={true}
                  autoFocus={false}
                  className={classNames(
                    "inline-flex h-6 w-6 items-center justify-center rounded-lg",
                    "bg-orange-50 text-orange-400 dark:bg-orange-900/30 dark:text-orange-300",
                    {
                      "bg-red-50 text-red-400 dark:bg-red-900/30 dark:text-red-300": hasError,
                      "pointer-events-none": loading,
                    },
                  )}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleBookmarkForIndicator();
                  }}
                >
                  <div>
                    {isBookmarked ? (
                      <BookmarkIcon fill="currentColor" className="size-3.5 fill-orange-400" />
                    ) : (
                      <BookmarkIcon className="size-3.5" />
                    )}
                  </div>
                </TooltipTrigger>

                <TooltipContent className="rounded-lg bg-gray-600 p-2 font-aeonik text-xs" side="top">
                  <span className="font-bold tracking-wider text-white">
                    {getLabelForBookmarkTooltip(isBookmarked, hasError, verbiageLanguage)}
                  </span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {viewerCanSeeHistory && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger
                  autoFocus={false}
                  asChild={true}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-purple-50 text-purple-400 dark:bg-purple-900/30 dark:text-purple-300"
                >
                  <div>
                    <LineChartIcon className="h-4 w-4" strokeWidth={3} />
                  </div>
                </TooltipTrigger>

                <TooltipContent className="rounded-lg bg-gray-600 p-2 font-aeonik text-xs" side="top">
                  <span className="font-bold tracking-wider text-white">Ver Histórico</span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
      <h2 className={classNames("font-medium text-gray-800 dark:text-slate-200")}>{abbreviation}</h2>
      <p className="line-clamp-2 select-none text-left text-sm font-normal italic leading-5 text-gray-400 dark:text-slate-400">
        {description}
      </p>
      {category && (
        <span className="inline-flex rounded-lg bg-violet-50 px-3 py-1 text-xs font-medium tracking-wide text-violet-900 dark:bg-violet-900/30 dark:text-violet-300">
          {getTabName(intl, category)}
        </span>
      )}
    </>
  );

  if (viewerCanSeeHistory) {
    return (
      <button
        type="button"
        className={rootClassName}
        onClick={(event) => {
          event.stopPropagation();

          onToggleActive();
        }}
        {...props}
      >
        {children}
      </button>
    );
  }
  return <div className={rootClassName}>{children}</div>;
}

export function getLabelForBookmarkTooltip(
  isBookmarked: boolean,
  hasError: boolean,
  verbiageLanguage: IValuationIndicatorVerbiageLanguage,
) {
  if (hasError) {
    if (verbiageLanguage === IValuationIndicatorVerbiageLanguage.EnUs) {
      return `Couldn't save the indicator. Please try again later`;
    }
    return "Não foi possível salvar o indicador. Tente novamente";
  }
  if (verbiageLanguage === IValuationIndicatorVerbiageLanguage.EnUs) {
    return isBookmarked ? "Remove Indicator" : "Save Indicator";
  }
  return isBookmarked ? "Remover Indicador" : "Salvar Indicador";
}
